export const MAX_QUANTITY_NUMBER = 1000;

export enum ParseStatus {
  ParseOk = "ParseOk",
  ParseErrorArticleNumber = "ParseErrorArticleNumber",
  ParseErrorQuantity = "ParseErrorQuantity",
}

export type ProductArticleIdAndQuantity = {
  productArticleId: string;
  quantity: number;
  parentArticle?: string;
  articlesCountInCombination?: number;
};

type ValidateInputOptions = {
  validateArticleNumber?: boolean;
};

const cleanUpArticleId = (articleId: string) => {
  return articleId.replace(/\./g, "").replace(/-/g, "").trim();
};

export const extractProductArticleIdAndQuantity = (
  inputValue: string
): ProductArticleIdAndQuantity[] => {
  return inputValue
    .trim()
    .replace(/\t/g, " ")
    .replace(/ +/g, " ")
    .split(/\n/)
    .map((line) => {
      const [productArticleId, quantity] = line.trim().split(/\s+/);
      const cleanedId = cleanUpArticleId(productArticleId ?? "");
      return {
        productArticleId: cleanedId,
        quantity: quantity ? parseInt(quantity, 10) : 1,
      };
    });
};

export const extractArticleNumbersOnly = (
  inputValue: string
): ProductArticleIdAndQuantity[] => {
  return inputValue.split(/\r?\n/).flatMap((line) => {
    const rawArticleId = line.trim().split(/\s+/)[0];
    if (!rawArticleId) return [];

    return [
      {
        productArticleId: cleanUpArticleId(rawArticleId),
        quantity: 1,
      },
    ];
  });
};

export const validateArticleNumbersOnly = (inputValue: string): ParseStatus => {
  const articleIds = inputValue
    .split(/\r?\n/)
    .map((line) => line.trim().split(/\s+/)[0])
    .filter((articleId): articleId is string => !!articleId);

  if (articleIds.length === 0) return ParseStatus.ParseErrorArticleNumber;

  return articleIds.every(
    (articleId) =>
      /^[0-9.-]+$/.test(articleId) && cleanUpArticleId(articleId).length === 8
  )
    ? ParseStatus.ParseOk
    : ParseStatus.ParseErrorArticleNumber;
};

export const isValidInput = (
  inputValue: string,
  { validateArticleNumber = true }: ValidateInputOptions = {}
): ParseStatus => {
  if (!inputValue) return ParseStatus.ParseErrorArticleNumber;

  const lines = inputValue.trim().split(/\n/);
  const productArticleIdRegex = /^[0-9.-]+$/;
  const quantityRegex = /^[0-9]+$/;

  for (const line of lines) {
    const [productArticleId, quantity] = line.trim().split(/\s+/);
    const rawProductArticleId = productArticleId ?? "";
    const cleanedProductArticleId = cleanUpArticleId(rawProductArticleId);

    const isArticleNumber =
      productArticleIdRegex.test(rawProductArticleId) &&
      cleanedProductArticleId.length === 8;

    if (validateArticleNumber && !isArticleNumber) {
      return ParseStatus.ParseErrorArticleNumber;
    }

    if (quantity) {
      if (!quantityRegex.test(quantity)) {
        return ParseStatus.ParseErrorQuantity;
      }
      const quantityNumber = parseInt(quantity, 10);
      if (quantityNumber < 1 || quantityNumber > MAX_QUANTITY_NUMBER) {
        return ParseStatus.ParseErrorQuantity;
      }
    }
  }

  return ParseStatus.ParseOk;
};
